import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { LocationsCard } from '../components/Locations.js';
import {
  AvailabilityActions,
  PeriodStrip,
  ResponseByLocation,
  ResponseChart,
  StatusTiles,
} from '../components/Availability.js';
import { Card, LoadError, Loading, Notice, SelectField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime, formatDuration } from '../i18n.js';
import { useMe } from '../session.js';

type Filter = 'all' | 'open' | 'resolved';
type Incident = Schemas['Incident'];
type DayState = 'ok' | 'degraded' | 'down' | 'none';
interface Uptime {
  range: string;
  uptimePct: number | null;
  series: { t: string; checks: number; failed: number }[];
}

const DAY_MS = 86_400_000;
const DAY_CLASS: Record<DayState, string> = {
  ok: 'bg-green-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-600',
  none: 'bg-slate-200',
};

/** Days are UTC calendar days, the same buckets the uptime series uses. */
const dayKey = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const endOf = (i: Incident, now: number) => (i.resolvedAt ? Date.parse(i.resolvedAt) : now);

/** 90 days of uptime; plans with less history fall back to the 7 days every plan shows. */
async function fetchUptime(siteId: string): Promise<Uptime> {
  const get = (range: '90d' | '7d') =>
    unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } }));
  try {
    return await get('90d');
  } catch (e) {
    if (e instanceof ApiError && e.code === 'PLAN_LIMIT_REACHED') return get('7d');
    throw e;
  }
}

/** One state per day for the last `days` days, oldest first. */
export function calendarDays(
  days: number,
  series: Uptime['series'],
  incidents: Incident[],
  now: number,
): { day: string; state: DayState }[] {
  const checks = new Map<string, { checks: number; failed: number }>();
  for (const b of series) {
    const k = dayKey(Date.parse(b.t));
    const c = checks.get(k) ?? { checks: 0, failed: 0 };
    checks.set(k, { checks: c.checks + b.checks, failed: c.failed + b.failed });
  }
  const today = Date.parse(`${dayKey(now)}T00:00:00Z`);
  return Array.from({ length: days }, (_, n) => {
    const start = today - (days - 1 - n) * DAY_MS;
    const day = dayKey(start);
    const hits = incidents.filter(
      (i) => Date.parse(i.startedAt) < start + DAY_MS && endOf(i, now) >= start,
    );
    const c = checks.get(day);
    const state: DayState = hits.some((i) => i.kind === 'downtime')
      ? 'down'
      : hits.length > 0 || (c && c.failed > 0)
        ? 'degraded'
        : c && c.checks > 0
          ? 'ok'
          : 'none';
    return { day, state };
  });
}

/** Outage figures for the tiles: count, total and longest downtime within the window. */
export function outageStats(incidents: Incident[], since: number, now: number) {
  const seconds = incidents
    .filter((i) => i.kind === 'downtime' && endOf(i, now) >= since)
    .map((i) => Math.max(0, endOf(i, now) - Math.max(since, Date.parse(i.startedAt))) / 1000);
  return {
    count: seconds.length,
    total: seconds.reduce((a, b) => a + b, 0),
    longest: seconds.reduce((a, b) => Math.max(a, b), 0),
  };
}

/** S5: outages as KPI tiles, a calendar of the last days and a history timeline. */
export function Incidents() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [filter, setFilter] = useState<Filter>('all');
  const incidents = useQuery({
    queryKey: ['site', siteId, 'incidents', 'all'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/incidents', {
          params: { path: { siteId }, query: { status: 'all', limit: 200 } },
        }),
      ),
  });
  const uptime = useQuery({
    queryKey: ['site', siteId, 'uptime', 'calendar'],
    queryFn: () => fetchUptime(siteId),
  });

  const items = incidents.data?.items ?? [];
  const shown = items.filter((i) =>
    filter === 'open' ? !i.resolvedAt : filter === 'resolved' ? !!i.resolvedAt : true,
  );

  return (
    <>
      <PageTitle actions={<AvailabilityActions siteId={siteId} />}>
        {t('availability.title')}
      </PageTitle>
      <div className="mb-4 flex flex-col gap-4">
        <StatusTiles siteId={siteId} />
        <PeriodStrip siteId={siteId} incidents={incidents.data?.items ?? []} />
        <ResponseChart siteId={siteId} />
        <ResponseByLocation siteId={siteId} />
      </div>
      <LocationsCard siteId={siteId} />
      {incidents.isPending ? <Loading /> : null}
      {incidents.isError ? (
        <LoadError
          message={errorMessage(incidents.error, t)}
          onRetry={() => void incidents.refetch()}
        />
      ) : null}
      {incidents.data ? (
        <div className="flex flex-col gap-4">
          {uptime.data ? <Calendar items={items} uptime={uptime.data} /> : null}
          <Card>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <h2 className="text-lg font-semibold">{t('incidents.history')}</h2>
              <div className="w-full max-w-xs">
                <SelectField
                  label={t('incidents.filter')}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as Filter)}
                >
                  {(['all', 'open', 'resolved'] as const).map((f) => (
                    <option key={f} value={f}>
                      {t(`incidents.filters.${f}`)}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
            {shown.length === 0 ? (
              <Notice kind="success">{t('incidents.empty')}</Notice>
            ) : (
              <ol className="flex flex-col">
                {shown.map((i, n) => (
                  <Entry
                    key={i.id}
                    incident={i}
                    last={n === shown.length - 1}
                    language={i18n.language}
                    timeZone={timeZone}
                  />
                ))}
              </ol>
            )}
            {shown.some((i) => i.confidence !== 'multi_region') ? (
              <p className="mt-4 text-sm text-slate-700">{t('incidents.confidence')}</p>
            ) : null}
          </Card>
        </div>
      ) : null}
    </>
  );
}

function Calendar({ items, uptime }: { items: Incident[]; uptime: Uptime }) {
  const { t, i18n } = useTranslation();
  const days = uptime.range === '7d' ? 7 : 90;
  const cells = calendarDays(days, uptime.series, items, Date.now());
  const date = (day: string) => formatDate(`${day}T12:00:00Z`, i18n.language, 'UTC');
  const count = (s: DayState) => cells.filter((c) => c.state === s).length;
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('incidents.calendar.title', { days })}</h2>
        <ul className="flex flex-wrap gap-4 text-sm text-slate-700">
          {(['ok', 'degraded', 'down'] as const).map((s) => (
            <li key={s} className="flex items-center gap-1.5">
              <span aria-hidden="true" className={`size-3 rounded-sm ${DAY_CLASS[s]}`} />
              {t(`incidents.calendar.${s}`)}
            </li>
          ))}
        </ul>
      </div>
      <div
        role="img"
        aria-label={t('incidents.calendar.summary', {
          days,
          ok: count('ok'),
          degraded: count('degraded'),
          down: count('down'),
        })}
        className={`mt-4 grid gap-1 ${days === 90 ? 'grid-cols-15 sm:grid-cols-30' : 'grid-cols-7'}`}
      >
        {cells.map((c) => (
          <span
            key={c.day}
            data-state={c.state}
            title={`${date(c.day)}: ${t(`incidents.calendar.${c.state}`)}`}
            className={`h-6 rounded-sm ${DAY_CLASS[c.state]}`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-xs text-slate-700">
        <span>{cells[0] ? date(cells[0].day) : null}</span>
        <span>{t('incidents.calendar.today')}</span>
      </div>
      {days < 90 ? (
        <p className="mt-3 text-sm text-slate-700">{t('incidents.calendar.limited')}</p>
      ) : null}
    </Card>
  );
}

function Entry({
  incident: i,
  last,
  language,
  timeZone,
}: {
  incident: Incident;
  last: boolean;
  language: string;
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const dot = !i.resolvedAt || i.kind === 'downtime' ? 'bg-red-600' : 'bg-amber-500';
  const seconds =
    i.durationSeconds ?? (i.resolvedAt ? null : (Date.now() - Date.parse(i.startedAt)) / 1000);
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center pt-1.5" aria-hidden="true">
        <span className={`size-3.5 shrink-0 rounded-full ${dot}`} />
        {last ? null : <span className="mt-1 w-0.5 flex-1 bg-slate-200" />}
      </div>
      <div className={`flex min-w-0 flex-1 flex-col gap-1 ${last ? '' : 'pb-5'}`}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="font-semibold">
            {t(`incidents.kinds.${i.kind}`, { defaultValue: i.kind })} –{' '}
            {t(`incidents.causes.${i.causeCode}`, {
              defaultValue: t('incidents.causes.fallback'),
            })}
          </h3>
          <span className="text-sm text-slate-700">
            {formatDateTime(i.startedAt, language, timeZone)} –{' '}
            {i.resolvedAt ? formatDateTime(i.resolvedAt, language, timeZone) : '…'}
            {seconds === null ? null : ` · ${formatDuration(seconds, language)}`}
          </span>
        </div>
        {i.explanation?.what ? (
          <p className="text-sm text-slate-700">{i.explanation.what}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {i.resolvedAt ? (
            <span className="text-slate-700">{t('incidents.resolved')}</span>
          ) : (
            <strong className="text-red-800">{t('incidents.ongoing')}</strong>
          )}
          {i.flapping ? <span className="text-slate-700">({t('incidents.unstable')})</span> : null}
          {i.confidence === 'multi_region' ? (
            <span
              className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-900"
              title={t('incidents.multiRegionTitle')}
            >
              {t('incidents.multiRegion')}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}
